// Theme preference + application (board 34 erp/today--dark; HANDOFF-erp-web §24).
//
// The dark tokens live in styles/tokens.css under `[data-theme="dark"]`; this
// module decides what <html data-theme> says. Two inputs:
//   · the person's preference — `localStorage.erp_theme` = light | dark | system
//     (default system; garbage reads as system). Per device, not per account.
//   · the platform flag `ui.dark-mode` (registry: «المستخدم يختار، والمفتاح يتيح
//     الخيار» — the flag enables the CHOICE). While it is off the page is light
//     whatever is stored, and the «المظهر» row is absent (rule 6).
// `system` follows prefers-color-scheme live. Applied once at the shell root
// (AppShell → useTheme(flag)); the appearance screen binds to the same store.

import { useEffect, useSyncExternalStore } from 'react';
import { readStore, writeStore } from './storage';

export type ThemePref = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'erp_theme';
export const DARK_MODE_FLAG = 'ui.dark-mode';
export const DARK_SCHEME_QUERY = '(prefers-color-scheme: dark)';

const PREFS: readonly ThemePref[] = ['system', 'light', 'dark'];

export function isThemePref(v: unknown): v is ThemePref {
  return typeof v === 'string' && (PREFS as readonly string[]).includes(v);
}

/** The stored preference; anything missing or unknown reads as `system`. */
export function readThemePref(): ThemePref {
  const raw = readStore(THEME_STORAGE_KEY);
  return isThemePref(raw) ? raw : 'system';
}

// The preference store: the value lives in storage; subscribers learn of a write.
const listeners = new Set<() => void>();

export function writeThemePref(pref: ThemePref): void {
  writeStore(THEME_STORAGE_KEY, pref);
  listeners.forEach((l) => l());
}

function subscribePref(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** light / dark for a preference, given whether the OS prefers dark. */
export function resolveTheme(pref: ThemePref, prefersDark: boolean): ResolvedTheme {
  if (pref === 'dark') return 'dark';
  if (pref === 'light') return 'light';
  return prefersDark ? 'dark' : 'light';
}

function darkScheme(): MediaQueryList | null {
  try {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
    return window.matchMedia(DARK_SCHEME_QUERY) ?? null;
  } catch {
    return null;
  }
}

/** Does the OS prefer dark right now? (false where matchMedia is missing.) */
export function systemPrefersDark(): boolean {
  return darkScheme()?.matches === true;
}

function subscribeScheme(cb: () => void): () => void {
  const mql = darkScheme();
  if (!mql) return () => {};
  if (typeof mql.addEventListener === 'function') {
    mql.addEventListener('change', cb);
    return () => mql.removeEventListener('change', cb);
  }
  // Safari < 14
  mql.addListener(cb);
  return () => mql.removeListener(cb);
}

/** Stamp <html data-theme>: the resolved preference while the flag is on, light otherwise. */
export function applyTheme(pref: ThemePref, flagOn: boolean): ResolvedTheme {
  const resolved: ResolvedTheme = flagOn ? resolveTheme(pref, systemPrefersDark()) : 'light';
  if (typeof document !== 'undefined') document.documentElement.dataset.theme = resolved;
  return resolved;
}

export interface ThemeApi {
  pref: ThemePref;
  setPref: (pref: ThemePref) => void;
  /** What the page shows now: the pref resolved while `flagOn`, else light. */
  resolved: ResolvedTheme;
}

/**
 * The preference, its setter and the resolved theme; re-applies <html
 * data-theme> on a pref change, on a prefers-color-scheme change (while the
 * pref is system) and when the flag changes. Idempotent across consumers.
 */
export function useTheme(flagOn: boolean): ThemeApi {
  const pref = useSyncExternalStore(subscribePref, readThemePref, () => 'system' as ThemePref);
  const prefersDark = useSyncExternalStore(subscribeScheme, systemPrefersDark, () => false);
  const resolved: ResolvedTheme = flagOn ? resolveTheme(pref, prefersDark) : 'light';
  useEffect(() => {
    document.documentElement.dataset.theme = resolved;
  }, [resolved]);
  return { pref, setPref: writeThemePref, resolved };
}
